import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Personal } from '../types';
import {
  X,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Building,
  Heart,
  Code,
  Edit,
  Save,
  XCircle,
  Activity,
  Clock,
  CheckCircle,
  AlertCircle,
  BarChart3,
  RefreshCw,
  Loader,
  TrendingUp,
  Users
} from 'lucide-react';
import { useMsal } from '@azure/msal-react';
import { useAzureDevOps } from '../services/azureDevOpsService';
import ActivityBreakdownChart from './ActivityBreakdownChart';

interface PersonalDetailModalProps {
  personal: Personal;
  isOpen: boolean;
  onClose: () => void;
  userPhoto?: string;
  onPersonalUpdated?: (updatedPersonal: Personal) => void;
}

const COLORS = ['#2563EB', '#16A34A', '#CA8A04', '#DC2626', '#737373', '#8B5CF6', '#EC4899'];

const PersonalDetailModal: React.FC<PersonalDetailModalProps> = ({
  personal,
  isOpen,
  onClose,
  userPhoto,
  onPersonalUpdated
}) => {
  const { accounts } = useMsal();
  const currentUserEmail = accounts[0]?.username?.toLowerCase();
  const isOwnProfile = personal.email?.toLowerCase() === currentUserEmail;

  const [isEditing, setIsEditing] = useState(false);
  const [editedPersonal, setEditedPersonal] = useState<Personal>(personal);
  const [localPersonal, setLocalPersonal] = useState<Personal>(personal);
  const [loading, setLoading] = useState(false);

  // Azure DevOps state
  const [capacityData, setCapacityData] = useState<any>(null);
  const [devOpsLoading, setDevOpsLoading] = useState(false);
  const [devOpsError, setDevOpsError] = useState<string | null>(null);
  const mountedRef = useRef(false);
  const isDevOpsLoadingRef = useRef(false);
  const { isConfigured, getUserCapacityData, refreshUserData } = useAzureDevOps();

  // Cargar datos completos del perfil cuando se abre el modal y es el perfil propio
  useEffect(() => {
    if (!isOpen) return;

    const loadFullPersonalData = async () => {
      if (isOwnProfile && personal.email) {
        setLoading(true);
        try {
          
          // Importar dinámicamente ApiService
          const { ApiService } = await import('../services/api');
          
          // Cargar datos completos del usuario por ID
          const fullPersonalData = await ApiService.getPersonalById(personal.id);
          
          if (fullPersonalData) {
            setLocalPersonal(fullPersonalData);
            setEditedPersonal(fullPersonalData);
          } else {
            // Si no se encontraron datos por email, usar los datos originales
            setLocalPersonal(personal);
            setEditedPersonal(personal);
          }
        } catch (error) {
          // En caso de error, usar los datos originales
          setLocalPersonal(personal);
          setEditedPersonal(personal);
        } finally {
          setLoading(false);
        }
      } else {
        // Si no es el perfil propio, usar datos originales
        setLocalPersonal(personal);
        setEditedPersonal(personal);
      }
      setIsEditing(false);
    };

    loadFullPersonalData();
  }, [personal, isOpen, isOwnProfile, currentUserEmail]);

  // Cargar datos de Azure DevOps cuando se abre el modal
  const fetchDevOpsData = useCallback(async (forceRefresh = false) => {
    if (!personal.email || isDevOpsLoadingRef.current) return;

    isDevOpsLoadingRef.current = true;
    setDevOpsLoading(true);
    setDevOpsError(null);

    try {
      const data = forceRefresh
        ? await refreshUserData(personal.email)
        : await getUserCapacityData(personal.email);

      setCapacityData(data);

      if (!data) {
        setDevOpsError('Usuario no encontrado en equipos de Azure DevOps');
      }
    } catch (err: any) {
      setDevOpsError(err.message || 'Error al obtener datos de Azure DevOps');
    } finally {
      isDevOpsLoadingRef.current = false;
      setDevOpsLoading(false);
    }
  }, [personal.email, getUserCapacityData, refreshUserData]);

  useEffect(() => {
    if (isOpen && personal.email && isConfigured() && !mountedRef.current) {
      mountedRef.current = true;
      fetchDevOpsData();
    }

    if (!isOpen) {
      mountedRef.current = false;
    }
  }, [isOpen, personal.email, isConfigured, fetchDevOpsData]);

  if (!isOpen) return null;

  const handleSave = async () => {
    try {
      // Preparar datos para enviar al backend
      const profileUpdateData = {
        apodo: editedPersonal.apodo,
        telefono: editedPersonal.telefono,
        ciudad: editedPersonal.ciudad,
        biografia: editedPersonal.biografia,
        intereses: editedPersonal.intereses,
        habilidades: editedPersonal.habilidades,
        idiomas: editedPersonal.idiomas,
        redesSociales: editedPersonal.redesSociales,
        hobbies: editedPersonal.hobbies,
        comidaFavorita: editedPersonal.comidaFavorita,
        musicaFavorita: editedPersonal.musicaFavorita,
        peliculasSeries: editedPersonal.peliculasSeries,
        deportes: editedPersonal.deportes,
        tipoPersonalidad: editedPersonal.tipoPersonalidad,
        zonaHoraria: editedPersonal.zonaHoraria,
        estadoCivil: editedPersonal.estadoCivil,
        fechaNacimiento: editedPersonal.fechaNacimiento
      };

      // Importar dinámicamente ApiService
      const { ApiService } = await import('../services/api');
      
      // Llamar a la API para actualizar el perfil
      const updatedPersonal = await ApiService.updatePersonalProfile(personal.id, profileUpdateData);
      
      console.log('✅ Perfil actualizado exitosamente:', updatedPersonal);
      
      // Actualizar los datos locales
      setEditedPersonal(updatedPersonal);
      setLocalPersonal(updatedPersonal);
      if (onPersonalUpdated) {
        onPersonalUpdated(updatedPersonal);
      }
      
      setIsEditing(false);
      
    } catch (error) {
      console.error('❌ Error al actualizar el perfil:', error);
      // TODO: Mostrar mensaje de error al usuario
    }
  };

  const handleCancel = () => {
    setEditedPersonal(localPersonal);
    setIsEditing(false);
  };

  const updateArrayField = (field: keyof Personal, newValue: string) => {
    const currentArray = (editedPersonal[field] as string[]) || [];
    if (!currentArray.includes(newValue.trim()) && newValue.trim()) {
      setEditedPersonal({
        ...editedPersonal,
        [field]: [...currentArray, newValue.trim()]
      });
    }
  };

  const removeFromArray = (field: keyof Personal, index: number) => {
    const currentArray = (editedPersonal[field] as string[]) || [];
    setEditedPersonal({
      ...editedPersonal,
      [field]: currentArray.filter((_, i) => i !== index)
    });
  };

  const getInitials = (nombre: string, apellido?: string) => {
    return `${nombre.charAt(0)}${apellido ? apellido.charAt(0) : ''}`.toUpperCase();
  };


  const formatDate = (dateString?: string) => {
    if (!dateString) return 'No especificada';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleCardClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-neutral-900/50 dark:bg-black/70 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative w-full max-w-3xl bg-white dark:bg-neutral-900 rounded-lg shadow-md transition-all"
          onClick={handleCardClick}
        >
          {/* Header */}
          <div className="relative p-6 pb-4 border-b border-neutral-200 dark:border-neutral-700">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-4">
                {/* Avatar */}
                <div className="relative">
                  <div className="w-16 h-16 rounded-lg overflow-hidden border border-neutral-200 dark:border-neutral-700">
                    {userPhoto ? (
                      <img
                        src={userPhoto}
                        alt={`${localPersonal.nombre} ${localPersonal.apellido || ''}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center">
                        <span className="text-neutral-600 dark:text-neutral-300 font-semibold text-lg">
                          {getInitials(localPersonal.nombre, localPersonal.apellido)}
                        </span>
                      </div>
                    )}
                  </div>
                  {/* Status */}
                  <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white dark:border-neutral-900 ${
                    localPersonal.disponible ? 'bg-success' : 'bg-error'
                  }`}></div>
                </div>

                {/* Basic Info */}
                <div>
                  <div className="flex items-center space-x-3 mb-1">
                    <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">
                      {localPersonal.nombre} {localPersonal.apellido}
                    </h1>
                    {(isEditing ? editedPersonal.apodo : localPersonal.apodo) && (
                      <span className="text-lg text-neutral-500 dark:text-neutral-400 font-normal">"{isEditing ? editedPersonal.apodo : localPersonal.apodo}"</span>
                    )}
                  </div>
                  <p className="text-lg text-neutral-600 dark:text-neutral-400 font-medium mb-1">{localPersonal.rol}</p>
                  {localPersonal.area && (
                    <div className="flex items-center space-x-2 text-sm text-neutral-500 dark:text-neutral-400">
                      <Building size={14} />
                      <span>{localPersonal.area.nombre}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex items-center space-x-2">
                {loading && (
                  <div className="flex items-center space-x-2 text-neutral-500 dark:text-neutral-400 text-sm">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-neutral-300 dark:border-neutral-600 border-t-primary"></div>
                    <span>Cargando perfil...</span>
                  </div>
                )}

                {isOwnProfile && !loading && (
                  <button
                    onClick={() => isEditing ? handleSave() : setIsEditing(true)}
                    className={isEditing ? 'btn-primary flex items-center gap-2' : 'btn-secondary flex items-center gap-2'}
                  >
                    {isEditing ? (
                      <>
                        <Save size={16} />
                        <span>Guardar</span>
                      </>
                    ) : (
                      <>
                        <Edit size={16} />
                        <span>Editar</span>
                      </>
                    )}
                  </button>
                )}

                {isEditing && (
                  <button
                    onClick={handleCancel}
                    className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-md transition-all text-neutral-600 dark:text-neutral-400"
                  >
                    <XCircle size={16} />
                  </button>
                )}

                <button
                  onClick={onClose}
                  className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-md transition-all text-neutral-500 dark:text-neutral-400"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Left Column - Contact & Personal Info */}
              <div className="space-y-6">
                {/* Contact Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 flex items-center">
                    <Mail size={18} className="mr-2 text-neutral-400 dark:text-neutral-500" />
                    Contacto
                  </h3>

                  <div className="space-y-3">
                    {localPersonal.email && (
                      <div className="flex items-center space-x-3 py-2">
                        <Mail size={16} className="text-neutral-400 dark:text-neutral-500" />
                        <a href={`mailto:${localPersonal.email}`} className="text-primary dark:text-primary-dark hover:underline font-medium">
                          {localPersonal.email}
                        </a>
                      </div>
                    )}

                    <div className="flex items-center space-x-3 py-2">
                      <Phone size={16} className="text-neutral-400 dark:text-neutral-500" />
                      {isEditing ? (
                        <input
                          type="tel"
                          value={editedPersonal.telefono || ''}
                          onChange={(e) => setEditedPersonal({...editedPersonal, telefono: e.target.value})}
                          className="input-field flex-1"
                          placeholder="Agregar teléfono"
                        />
                      ) : (
                        <span className="text-neutral-700 dark:text-neutral-300">{localPersonal.telefono || 'No especificado'}</span>
                      )}
                    </div>

                    <div className="flex items-center space-x-3 py-2">
                      <MapPin size={16} className="text-neutral-400 dark:text-neutral-500" />
                      {isEditing ? (
                        <input
                          type="text"
                          value={editedPersonal.ciudad || ''}
                          onChange={(e) => setEditedPersonal({...editedPersonal, ciudad: e.target.value})}
                          className="input-field flex-1"
                          placeholder="Agregar ciudad"
                        />
                      ) : (
                        <span className="text-neutral-700 dark:text-neutral-300">{localPersonal.ciudad || 'No especificada'}</span>
                      )}
                    </div>

                    {localPersonal.fechaIngreso && (
                      <div className="flex items-center space-x-3 py-2">
                        <Calendar size={16} className="text-neutral-400 dark:text-neutral-500" />
                        <div className="text-neutral-700 dark:text-neutral-300">
                          <span className="text-sm text-neutral-500 dark:text-neutral-400">Ingreso: </span>
                          <span>{formatDate(localPersonal.fechaIngreso)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Biography */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 flex items-center">
                    <User size={18} className="mr-2 text-neutral-400 dark:text-neutral-500" />
                    Biografía
                  </h3>

                  {isEditing ? (
                    <textarea
                      value={editedPersonal.biografia || ''}
                      onChange={(e) => setEditedPersonal({...editedPersonal, biografia: e.target.value})}
                      className="input-field w-full resize-none"
                      rows={3}
                      placeholder="Cuéntanos sobre ti..."
                    />
                  ) : (
                    <p className="text-neutral-700 dark:text-neutral-300 leading-relaxed">
                      {localPersonal.biografia || 'No hay biografía disponible'}
                    </p>
                  )}
                </div>
              </div>

              {/* Right Column - Skills & Interests */}
              <div className="space-y-6">
                {/* Skills */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 flex items-center">
                    <Code size={18} className="mr-2 text-neutral-400 dark:text-neutral-500" />
                    Habilidades
                  </h3>

                  <div className="flex flex-wrap gap-2">
                    {(localPersonal.habilidades || []).map((skill, index) => (
                      <span key={index} className="badge inline-flex items-center text-sm">
                        {skill}
                        {isEditing && (
                          <button
                            onClick={() => removeFromArray('habilidades', index)}
                            className="ml-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 text-lg leading-none"
                          >
                            ×
                          </button>
                        )}
                      </span>
                    ))}
                    {isEditing && (
                      <input
                        type="text"
                        placeholder="+ Agregar habilidad"
                        className="input-field px-3 py-1 text-sm"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            updateArrayField('habilidades', e.currentTarget.value);
                            e.currentTarget.value = '';
                          }
                        }}
                      />
                    )}
                  </div>
                </div>

                {/* Interests */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 flex items-center">
                    <Heart size={18} className="mr-2 text-neutral-400 dark:text-neutral-500" />
                    Intereses
                  </h3>

                  <div className="flex flex-wrap gap-2">
                    {(localPersonal.hobbies || []).map((hobby, index) => (
                      <span key={index} className="badge inline-flex items-center text-sm">
                        {hobby}
                        {isEditing && (
                          <button
                            onClick={() => removeFromArray('hobbies', index)}
                            className="ml-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 text-lg leading-none"
                          >
                            ×
                          </button>
                        )}
                      </span>
                    ))}
                    {isEditing && (
                      <input
                        type="text"
                        placeholder="+ Agregar interés"
                        className="input-field px-3 py-1 text-sm"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            updateArrayField('hobbies', e.currentTarget.value);
                            e.currentTarget.value = '';
                          }
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>

            </div>

            {/* Azure DevOps Section - Full Width EXPANDIDA */}
            {localPersonal.email && isConfigured() && (
              <div className="mt-8 pt-8 border-t border-neutral-200 dark:border-neutral-700">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 flex items-center">
                    <Activity size={20} className="mr-2 text-primary" />
                    Información de Azure DevOps
                  </h3>
                  <button
                    onClick={() => fetchDevOpsData(true)}
                    className="btn-secondary text-sm"
                    disabled={devOpsLoading}
                  >
                    <RefreshCw size={14} className={devOpsLoading ? 'animate-spin' : ''} />
                  </button>
                </div>

                {devOpsLoading ? (
                  <div className="flex items-center justify-center py-12 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
                    <Loader className="animate-spin text-primary mr-3" size={24} />
                    <span className="text-neutral-600 dark:text-neutral-400">Cargando datos de Azure DevOps...</span>
                  </div>
                ) : devOpsError ? (
                  <div className="bg-error/10 dark:bg-error/20 border border-error/30 rounded-lg p-6 text-center">
                    <div className="text-error mb-2">⚠️ {devOpsError}</div>
                    <button
                      onClick={() => fetchDevOpsData(true)}
                      className="mt-3 btn-secondary text-sm"
                    >
                      <RefreshCw size={14} className="mr-1" />
                      Reintentar
                    </button>
                  </div>
                ) : capacityData ? (
                  <div className="space-y-6">
                    {/* Header con equipo e iteración */}
                    <div className="bg-gradient-to-r from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 rounded-lg p-4 border border-primary/20 dark:border-primary/30">
                      <div className="flex items-center space-x-2 mb-1">
                        <Users className="text-primary" size={18} />
                        <h4 className="text-base font-semibold text-neutral-900 dark:text-neutral-50">
                          {capacityData.teamName}
                        </h4>
                      </div>
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">
                        Sprint: {capacityData.iterationName}
                      </p>
                    </div>

                    {/* Métricas principales en grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white dark:bg-neutral-800 rounded-lg p-4 border border-neutral-200 dark:border-neutral-700">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">Capacidad/día</span>
                          <Clock size={16} className="text-primary" />
                        </div>
                        <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">
                          {capacityData.metrics.totalCapacityPerDay}h
                        </p>
                      </div>

                      <div className="bg-white dark:bg-neutral-800 rounded-lg p-4 border border-neutral-200 dark:border-neutral-700">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">Completado</span>
                          <CheckCircle size={16} className="text-success" />
                        </div>
                        <p className="text-2xl font-bold text-success">
                          {capacityData.metrics.completedWork}h
                        </p>
                      </div>

                      <div className="bg-white dark:bg-neutral-800 rounded-lg p-4 border border-neutral-200 dark:border-neutral-700">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">Pendiente</span>
                          <AlertCircle size={16} className="text-warning" />
                        </div>
                        <p className="text-2xl font-bold text-warning">
                          {capacityData.metrics.remainingWork}h
                        </p>
                      </div>
                    </div>

                    {/* Gráfico expandido de actividades */}
                    {capacityData.metrics.activities.length > 0 && (
                      <div className="bg-white dark:bg-neutral-800 rounded-lg p-6 border border-neutral-200 dark:border-neutral-700">
                        <h5 className="text-base font-semibold text-neutral-900 dark:text-neutral-50 mb-6 flex items-center">
                          <BarChart3 className="mr-2 text-primary" size={20} />
                          Distribución de Actividades
                        </h5>

                        {capacityData.metrics.activities.length > 1 ? (
                          <div className="space-y-6">
                            {/* Gráfico más grande */}
                            <ActivityBreakdownChart
                              activities={capacityData.metrics.activities}
                              totalCapacity={capacityData.metrics.totalCapacityPerDay}
                              size="large"
                            />

                            {/* Tabla detallada */}
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead>
                                  <tr className="border-b border-neutral-200 dark:border-neutral-700">
                                    <th className="text-left py-2 px-3 text-xs font-medium text-neutral-500 dark:text-neutral-400">Actividad</th>
                                    <th className="text-right py-2 px-3 text-xs font-medium text-neutral-500 dark:text-neutral-400">Horas/día</th>
                                    <th className="text-right py-2 px-3 text-xs font-medium text-neutral-500 dark:text-neutral-400">% Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {capacityData.metrics.activities.map((activity: any, index: number) => {
                                    const percentage = ((activity.capacityPerDay / capacityData.metrics.totalCapacityPerDay) * 100).toFixed(1);
                                    return (
                                      <tr key={index} className="border-b border-neutral-100 dark:border-neutral-800">
                                        <td className="py-3 px-3">
                                          <div className="flex items-center">
                                            <div
                                              className="w-3 h-3 rounded-full mr-3"
                                              style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                            />
                                            <span className="text-sm font-medium text-neutral-900 dark:text-neutral-50">
                                              {activity.name}
                                            </span>
                                          </div>
                                        </td>
                                        <td className="py-3 px-3 text-right text-sm font-semibold text-neutral-900 dark:text-neutral-50">
                                          {activity.capacityPerDay}h
                                        </td>
                                        <td className="py-3 px-3 text-right text-sm text-neutral-600 dark:text-neutral-400">
                                          {percentage}%
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {capacityData.metrics.activities.map((activity: any, index: number) => (
                              <div key={index}>
                                <div className="flex justify-between items-center mb-2">
                                  <span className="text-sm font-medium text-neutral-900 dark:text-neutral-50">{activity.name}</span>
                                  <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">
                                    {activity.capacityPerDay}h/día (100%)
                                  </span>
                                </div>
                                <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-3">
                                  <div className="bg-primary h-3 rounded-full" style={{ width: '100%' }}></div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Sprint info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white dark:bg-neutral-800 rounded-lg p-4 border border-neutral-200 dark:border-neutral-700">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">Días del sprint</p>
                            <p className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">
                              {capacityData.metrics.sprintWorkDays} días laborables
                            </p>
                          </div>
                          <Calendar size={20} className="text-neutral-400 dark:text-neutral-500" />
                        </div>
                      </div>

                      <div className="bg-white dark:bg-neutral-800 rounded-lg p-4 border border-neutral-200 dark:border-neutral-700">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">Días restantes</p>
                            <p className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">
                              {capacityData.metrics.remainingWorkDays} días
                            </p>
                          </div>
                          <TrendingUp size={20} className="text-warning" />
                        </div>
                      </div>
                    </div>

                    {/* Utilización */}
                    <div className="bg-white dark:bg-neutral-800 rounded-lg p-4 border border-neutral-200 dark:border-neutral-700">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                          Utilización del sprint
                        </span>
                        <span className="text-lg font-bold text-neutral-900 dark:text-neutral-50">
                          {capacityData.metrics.utilizationPercent.toFixed(0)}%
                        </span>
                      </div>
                      <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-3">
                        <div
                          className={`h-3 rounded-full transition-all ${
                            capacityData.metrics.utilizationPercent < 70
                              ? 'bg-success'
                              : capacityData.metrics.utilizationPercent < 90
                              ? 'bg-warning'
                              : 'bg-error'
                          }`}
                          style={{ width: `${Math.min(capacityData.metrics.utilizationPercent, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-neutral-500 dark:text-neutral-400 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
                    No hay datos de Azure DevOps para este usuario
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800">
            <div className="flex justify-center">
              <div className="flex items-center space-x-2 text-sm font-medium text-neutral-700 dark:text-neutral-300">
                <div className={`w-2 h-2 rounded-full ${
                  localPersonal.disponible ? 'bg-success' : 'bg-error'
                }`}></div>
                <span>{localPersonal.disponible ? 'Disponible' : 'Ocupado'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalDetailModal;